import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import UserProfile from '../Auth/UserProfile';
import BedtimePairSheet from '../BedtimeContext/BedtimePairSheet';
import { BookIcon, CompassIcon, MicIcon, SparkleIcon } from '../icons';
import { Scene, Shell, ShellNavItem } from '../ui';
import { HomeIcon } from '../ui/uiIcons';
import { features } from '../../config/features';
import { ParentPreviewProvider, ParentPreviewDock, useParentPreview } from '../../context/ParentPreviewContext';
import './ParentShell.css';

export const STAGING_NOTE = 'Luma staging · Test accounts only';

const BEDTIME_NAV: ShellNavItem[] = [
  { label: 'Home', to: '/', icon: <HomeIcon size={22} />, end: true },
  { label: 'Discover', to: '/discover', icon: <CompassIcon size={22} /> },
  { label: 'Tonight', to: '/renders', icon: <SparkleIcon size={22} /> },
  { label: 'My stories', to: '/my-stories', icon: <BookIcon size={22} /> },
];

/* Fail-closed navigation while the bedtime bundle is switched off. */
const REPAIR_NAV: ShellNavItem[] = [
  { label: 'Family voices', to: '/voice', icon: <MicIcon size={22} /> },
  { label: 'Account & family', to: '/account', icon: <BookIcon size={22} /> },
];

/* Routes that keep their existing presentation inside the new shell. */
const LEGACY_PREFIXES = ['/admin'];
/* Management routes never auto-open the pair sheet and never show the chip. */
const MANAGEMENT_PREFIXES = ['/voice', '/account', '/admin'];

function startsWithAny(path: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Signed-in parent shell: the night scene, the rail or drawer, the account
 * menu as the one header utility, and the shared bedtime pair sheet. Child
 * story time renders outside this shell.
 */
const ParentShellContent: React.FC = () => {
  const preview = useParentPreview();
  const location = useLocation();
  const path = location.pathname;
  const billing = path === '/account/billing';
  const commerce = billing || path === '/my-stories';
  const personalMessages = /^\/voice\/[^/]+\/personalize\/[^/]+\/?$/.test(path);
  const sharingReview = path === '/admin/catalog-generation' || /^\/admin\/library\/[^/]+\/sharing\/?$/.test(path);
  const legacy = !billing && !sharingReview && startsWithAny(path, LEGACY_PREFIXES);
  const management = startsWithAny(path, MANAGEMENT_PREFIXES);
  const immersive = (path === '/discover' || path === '/my-stories') && new URLSearchParams(location.search).has('story');

  return (
    <Scene>
      <Shell
        mode="parent"
        player={preview?.activeRender ? <ParentPreviewDock /> : undefined}
        navItems={features.bedtimeContextV1 ? BEDTIME_NAV : REPAIR_NAV}
        brandTo="/"
        utilities={<UserProfile showFullProfile={false} />}
        notice={features.stagingMode ? commerce ? <><span className="luma-sr-only">{STAGING_NOTE} · </span>Test mode · no live payments</> : STAGING_NOTE : undefined}
        className={[personalMessages ? 'luma-shell--personal-messages' : '', path === '/household-stories' || sharingReview ? 'luma-shell--household-stories' : '', path === '/account' ? 'luma-shell--account' : '', commerce ? 'luma-shell--commerce' : '', billing ? 'luma-shell--billing' : legacy ? 'luma-shell--legacy' : immersive ? 'luma-shell--immersive' : ''].filter(Boolean).join(' ') || undefined}
      >
        {features.bedtimeContextV1 && <BedtimePairSheet autoOpen={!management} />}
        {legacy ? (
          <div className="luma-legacy" data-testid="luma-legacy-route">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </Shell>
    </Scene>
  );
};

const ParentShell: React.FC = () => features.bedtimeContextV1
  ? <ParentPreviewProvider><ParentShellContent /></ParentPreviewProvider>
  : <ParentShellContent />;

export default ParentShell;
